import { SelectOption } from "components/dropdown/Dropdown";
import { FormikContextType, useField, useFormikContext } from "formik";
import React, {
  useCallback,
  useEffect,
  useState,
  useRef,
  Dispatch,
} from "react";
import api from "api";
import Select from "react-select";
import { ValueType } from "react-select/src/types";
import {
  CustomControl,
  CustomOptionWithLink,
  CustomValue,
  SelectConfig,
  SelectConfigOptionalSearchParameters,
  customUseOnClickOutside,
} from "./SearchListRenderer";
import { debounce } from "lodash";
import { useOnceAfterCondition } from "../../../../helpers/useOnceAfterCondition";
import { useGetPermissions } from "../../../../helpers/useGetPermissions";
import {
  ActionTypes,
  selectFormReadOnlyFields,
  selectRelatedFormActionType,
  selectRelatedFormModuleName,
  selectRelatedFormOriginFormAutoSelectAllValuesFields,
  selectRelatedFormSuccessResponseIds,
  setOriginFormData,
  setRelatedFormActionType,
  setRelatedFormData,
  setRelatedFormModuleName,
  updateRelatedFormReduxData,
} from "reducers/listPage";
import { useDispatch, useSelector } from "react-redux";

import _ from "lodash";
import { NestedFormData } from "components/Entity/Entity";
import {
  CreatedDataParentFieldDataItem,
  NestedFormModuleData,
  NestedParentFormData,
  selectCreatedDataParentFieldData,
  selectNestedFormModuleData,
  selectNestedParentFormData,
  selectReloadNestedFormParentFormToggle,
  setCreatedDataParentFieldData,
  setNestedFormCreationContext,
  setNestedFormModuleData,
  setNestedParentFormData,
  setReloadNestedFormParentFormToggle,
} from "reducers/nestedCrudMenu";
import { APP_ROUTES, PARENT_CHILD_FIELD_MAPPING } from "config";
import useStoreProgrammmeId from "./useStoreProgrammeId";
import { SingleSelectOption } from "../SingleMultiSelectFieldRenderer.tsx/SingleMultiSelectFieldRenderer";
import ToastMessage, { toastConfig } from "helpers/ToastMessage/ToastMessage";
import { toast } from "react-toastify";

import editIcon from "assets/edit_icon.png";

type MultiSelectSearchListWithLinksProps = SelectConfig & {
  name: string;
  dataSource: string | null;
  singleSelect?: boolean;
  label?: string;
  afterLabelText?: string | null;
  readOnly?: boolean;
  editDisable?: boolean | undefined;
  expandable?: boolean;
  nestedFormModule?: { [fieldName: string]: APP_ROUTES };
  optionalSearchParameters?: SelectConfigOptionalSearchParameters;
  overrideOptions?: {
    [fieldName: string]: (
      formik: FormikContextType<any>
    ) => [SingleSelectOption[], string?, string?];
  };
  showEditIcon?: boolean;
  maxHeight?: string;
  addZIndexToMenu?: boolean;
  callSearchEndpointOnClick?: boolean;
};

type SingleSelectSearchListWithLinksProps = SelectConfig & {
  name: string;
  dataSource: string | null;
  singleSelect?: boolean;
  label?: string;
  afterLabelText?: string | null;
  readOnly?: boolean;
  nestedFormModule?: { [fieldName: string]: APP_ROUTES };
  optionalSearchParameters?: SelectConfigOptionalSearchParameters;
  overrideOptions?: {
    [fieldName: string]: (
      formik: FormikContextType<any>
    ) => [SingleSelectOption[], string?, string?];
  };
  showEditIcon?: boolean;
  maxHeight?: string;
  addZIndexToMenu?: boolean;
  callSearchEndpointOnClick?: boolean;
};

const saveFormReduxData = (
  formik: FormikContextType<any>,
  dispatch: Dispatch<any>
) => {
  if (formik.values) {
    const formData = _.cloneDeep(formik.values);
    const formId = formik.values?.id;
    let actionType = ActionTypes.UPDATE;

    if (!formId || formId === 0) {
      actionType = ActionTypes.CREATE;
    }

    const pathName = window?.location?.pathname;

    let moduleName = null;

    if (pathName) {
      moduleName = pathName.split("/")[1];
    }

    updateRelatedFormReduxData(formData, actionType, moduleName, dispatch);

    dispatch(
      setOriginFormData({
        pathName: pathName,
        data: formData,
        actionType: actionType,
        id: formId,
      })
    ); // The very first form's data to when we return to it we remember what values were entered
  }
};

export type OptionalSearchParameters = { [parameterName: string]: any };

const getOptionalSearchParameters = (
  optionalSearchParameters: SelectConfigOptionalSearchParameters | undefined,
  formik: FormikContextType<any>,
  name: string
) => {
  let parsedOptionalSearchParameters: OptionalSearchParameters | null = null;
  if (
    optionalSearchParameters &&
    typeof optionalSearchParameters === "object" &&
    optionalSearchParameters.hasOwnProperty(name) &&
    !parsedOptionalSearchParameters
  ) {
    const data = optionalSearchParameters[name];

    const tempParsedOptionalSearchParameters: OptionalSearchParameters = {};

    Object.keys(data).forEach((key: string) => {
      const valueKey = data[key];

      if (formik.values && formik.values.hasOwnProperty(valueKey)) {
        const value = formik.values[valueKey as keyof typeof formik.values];

        tempParsedOptionalSearchParameters[key] = value;
      }
    });

    if (Object.keys(tempParsedOptionalSearchParameters).length > 0) {
      parsedOptionalSearchParameters = tempParsedOptionalSearchParameters;
    }
    return parsedOptionalSearchParameters;
  }
};

export const MultiSelectSearchListWithLinks: React.FC<MultiSelectSearchListWithLinksProps> = ({
  name,
  label,
  dataSource,
  searchEndpoint,
  handleActionLinkClicked,
  onLinkClick,
  actionLinkConfig,
  linkTextConfig,
  singleSelect = false,
  afterLabelText,
  readOnly,
  editDisable,
  expandable,
  nestedFormModule,
  optionalSearchParameters,
  overrideOptions,
  showEditIcon,
  maxHeight,
  addZIndexToMenu,
  callSearchEndpointOnClick,
}) => {
  //set search endpoint
  const [parsedSearchEndpoint, setParsedSearchEndpoint] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (searchEndpoint) {
      if (
        typeof searchEndpoint === "object" &&
        searchEndpoint.hasOwnProperty(name)
      ) {
        setParsedSearchEndpoint(
          searchEndpoint[name as keyof typeof searchEndpoint]
        );
      } else if (typeof searchEndpoint === "string") {
        setParsedSearchEndpoint(searchEndpoint);
      }
    }
  }, [searchEndpoint]);

  const [{ value }, { touched, error, initialValue }, { setValue }] = useField<
    number[] | null
  >(name);

  const dispatch = useDispatch();

  const {
    values: formValues,
    setTouched,
    touched: formikTouched,
    setFieldValue,
    submitCount,
  } = useFormikContext<{
    [key: string]: any;
  }>();

  const hasError = submitCount > 0 && error;

  const [options, setOptions] = useState<SelectOption[] | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<SelectOption[] | null>(
    null
  );

  const defaultOptions = formValues[dataSource as keyof typeof formValues];

  const setExistingValues = () => {
    if (singleSelect) {
      if (initialValue) {
        const startOps = options?.filter(opt => initialValue === opt.value);

        setSelectedOptions(startOps ?? []);
        setFieldValue(name, initialValue);
      }
    }

    if (!value) return;

    if (!Array.isArray(value)) {
      const startOps = options?.filter(opt => value === opt.value);

      setSelectedOptions(startOps ?? []);
    } else if (Array.isArray(value) && value.length > 0) {
      const startOps = options?.filter(opt => value.includes(opt.value));

      setSelectedOptions(startOps ?? []);
    }
  };

  useOnceAfterCondition(
    () => {
      setExistingValues();
    },
    [options],
    options
  );

  const setDefaultOpts = () => {
    if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setOptions(defaultOptions);
    } else {
      setOptions([]);
    }
  };
  useEffect(() => {
    setDefaultOpts();
  }, []);
  //control search input
  const [searchString, setSearchString] = useState("");
  //control dropdown menu
  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const bind = customUseOnClickOutside(() => {
    setMenuOpen(false);
  }, true);

  const formik = useFormikContext();

  useEffect(() => {
    setMenuOpen(false);
  }, [selectedOptions]);

  const filterPlaceholder = (option: any, rawInput: string) => {
    return true;
  };

  const onChange = (v: SelectOption[] | SelectOption) => {
    let newOptions: any = v;

    let optionsToAdd: any = v;

    if (Array.isArray(newOptions)) {
      setSelectedOptions(newOptions);
    } else {
      setSelectedOptions([newOptions]);

      optionsToAdd = [optionsToAdd];
    }

    let allNewData = nestedParentFormData ? { ...nestedParentFormData } : {};

    if (
      nestedFormModule &&
      nestedFormModule.hasOwnProperty(name) &&
      allNewData.hasOwnProperty(nestedFormModule[name])
    ) {
      let newModuleData = { ...allNewData[nestedFormModule[name]] };

      newModuleData[name] = optionsToAdd;
      allNewData[nestedFormModule[name]] = newModuleData;
    } else if (nestedFormModule && nestedFormModule.hasOwnProperty(name)) {
      let newModuleData: any = {};

      newModuleData[name] = optionsToAdd;
      allNewData[nestedFormModule[name]] = newModuleData;
    }

    const currentPathName = window.location.pathname;

    let newCreatedDataParentFieldData = _.cloneDeep(createdDataParentFieldData);

    if (
      newCreatedDataParentFieldData &&
      createdDataParentFieldData &&
      currentPathName &&
      createdDataParentFieldData.hasOwnProperty(currentPathName)
    ) {
      let newData =
        createdDataParentFieldData[
          currentPathName as keyof typeof createdDataParentFieldData
        ];

      const optionsToAddIds =
        optionsToAdd && Array.isArray(optionsToAdd)
          ? optionsToAdd
              .map((item: any) => (item && item.value ? `${item.value}` : null))
              .filter(item => item !== null)
          : [];

      let optionsToKeep: any[] = [];

      if (Array.isArray(newData)) {
        newData.forEach(item => {
          if (
            item.fieldName !== name ||
            (item.fieldName && optionsToAddIds.includes(`${item.id}`))
          ) {
            optionsToKeep.push(item);
          }
        });
      }

      newCreatedDataParentFieldData[
        currentPathName as keyof typeof newCreatedDataParentFieldData
      ] = optionsToKeep;
    }

    dispatch(setCreatedDataParentFieldData(newCreatedDataParentFieldData));

    dispatch(setNestedParentFormData(allNewData));

    if (singleSelect) {
      if (Array.isArray(v) && v.length > 0) {
        setValue(v[0].value);
        setFieldValue(name, v[0].value);
      } else if (typeof v === "object" && v.hasOwnProperty("value")) {
        setValue([v["value" as keyof typeof v]]);
        setFieldValue(name, v["value" as keyof typeof v]);
      } else {
        setValue(null);
        setFieldValue(name, null);
      }
    } else {
      if (Array.isArray(v) && v.length > 0) {
        let finalValues = v.map(val => val.value);
        setValue(finalValues);
        setFieldValue(name, finalValues);
      } else {
        setValue(null);
        setFieldValue(name, null);
      }
    }
  };

  const nestedParentFormData = useSelector(selectNestedParentFormData);

  //control search
  const fetchResults = async (
    searchString: string,
    parsedSearchEndpoint: string,
    createdDataParentFieldData?: CreatedDataParentFieldDataItem[],
    existingValues?: SelectOption[],
    nestedParentFormData?: NestedParentFormData | null
  ) => {
    if (parsedSearchEndpoint) {
      let parameters: any = { searchString };

      const parsedOptionalSearchParameters = getOptionalSearchParameters(
        optionalSearchParameters,
        formik,
        name
      );

      if (parsedOptionalSearchParameters) {
        parameters = { ...parameters, ...parsedOptionalSearchParameters };
      }

      try {
        const results = await api.get(parsedSearchEndpoint, {
          params: parameters,
        });
        let options: SelectOption[] = [];

        if (
          results &&
          results.data &&
          results.data.status === "1" &&
          results.data.details.results &&
          Array.isArray(results.data.details.results)
        ) {
          options = results.data.details.results;
        }

        // Get IDs that backend says are valid for THIS record
        const validIdsFromBackend = options.map(opt => opt.value);

        if (createdDataParentFieldData) {
          // Only use Redux data if backend confirms it's valid
          let newSelectedOptions: SelectOption[] = createdDataParentFieldData
            .filter(item => {
              const itemId = typeof item.id === "string" ? parseInt(item.id, 10) : item.id;
              // Only keep if backend returned this ID
              return validIdsFromBackend.includes(itemId);
            })
            .map(item => {
              let newValue = item.id;

              if (typeof newValue === "string") {
                try {
                  newValue = parseInt(newValue, 10);
                } catch {
                  console.error(
                    "Failed to convert string to integer: ",
                    item.id
                  );
                }
              }

              return { label: item.searchString, value: newValue };
            });

          if (selectedOptions) {
            newSelectedOptions = [...newSelectedOptions, ...selectedOptions];
          }

          // Also validate nested form data
          if (
            nestedParentFormData &&
            nestedFormModule &&
            nestedFormModule.hasOwnProperty(name) &&
            nestedParentFormData.hasOwnProperty(nestedFormModule[name]) &&
            typeof nestedParentFormData[
              nestedFormModule[name] as keyof typeof formik.values
            ] === "object"
          ) {
            const data: any =
              nestedParentFormData[
                nestedFormModule[name] as keyof typeof formik.values
              ];

            if (data.hasOwnProperty(name) && Array.isArray(data[name])) {
              // Fix: Filter out items not in backend response
              const validNestedData = data[name]
                .filter((item: any) => item !== null && item !== undefined)
                .filter((item: any) => {
                  // Only include if backend returned this ID
                  return item.value && validIdsFromBackend.includes(item.value);
                })
                .map((item: any) => {
                  if (item.value && item.label) {
                    let newValue = item.value;
                    if (typeof newValue === "string") {
                      try {
                        newValue = parseInt(newValue, 10);
                      } catch {
                        console.error(
                          "Failed to convert string to integer: ",
                          item.value
                        );
                      }
                    }
                    return {
                      label: item.label,
                      value: newValue,
                    };
                  }
                  return null;
                })
                .filter((item: SelectOption | null): item is SelectOption => item !== null);

              newSelectedOptions = [...newSelectedOptions, ...validNestedData];
            }
          }

          let uniqueNewSelectedOptions: SelectOption[] = existingValues
            ? [...existingValues]
            : [];

          let uniqueNewSelectedOptionIds: number[] = existingValues
            ? existingValues.map(item => item.value)
            : [];

          newSelectedOptions.forEach(item => {
            if (!uniqueNewSelectedOptionIds.includes(item.value)) {
              uniqueNewSelectedOptions.push(item);
              uniqueNewSelectedOptionIds.push(item.value);
            }
          });

          setSelectedOptions(uniqueNewSelectedOptions);

          let ids = uniqueNewSelectedOptions.map(item => {
            let id: string | number = item.value;

            if (typeof id === "string") {
              id = parseInt(id, 10);
            }

            return id;
          });

          ids = [...new Set(ids)];

          formik.setFieldValue(name, ids);

          if (parsedSearchEndpoint) {
            setSearchString("");
            getSearchResults("", parsedSearchEndpoint);
          }
        }

        if (searchString === "" && existingValues) {
          const existingIds = existingValues.map(item => item.value);

          const tempExistingValuesInSearchResults = options.filter(item =>
            existingIds.includes(item.value)
          );

          options = [...options, ...tempExistingValuesInSearchResults];

          let newSelectedOptions = selectedOptions
            ? [...selectedOptions, ...tempExistingValuesInSearchResults]
            : [...tempExistingValuesInSearchResults];

          setSelectedOptions(newSelectedOptions);

          let newFormSelectedOptions = selectedOptions
            ? [
                ...selectedOptions,
                tempExistingValuesInSearchResults.map(item => item.value),
              ]
            : [...tempExistingValuesInSearchResults.map(item => item.value)];

          formik.setFieldValue(name, newFormSelectedOptions);
        }

        setOptions(options);
      } catch (error) {
        // Error case
        console.log("fetchResults error", error);
      }
    }
  };

  const getSearchResults = useCallback(debounce(fetchResults, 500), []);

  const onSearch = (e: any) => {
    e.preventDefault();
    if (!e.target.value) {
      setSearchString("");
      setDefaultOpts();

      if (parsedSearchEndpoint) {
        getSearchResults("", parsedSearchEndpoint);
      }
    } else if (parsedSearchEndpoint && e.target.value) {
      setSearchString(e.target.value);
      getSearchResults(e.target.value, parsedSearchEndpoint);
    }
  };

  // Nested form auto search data after creating new item:
  const reloadNestedFormParentFormToggle = useSelector(
    selectReloadNestedFormParentFormToggle
  );
  const createdDataParentFieldData = useSelector(
    selectCreatedDataParentFieldData
  );

  useEffect(() => {
    if (reloadNestedFormParentFormToggle && createdDataParentFieldData) {
      const currentPathName = window.location.pathname;

      if (createdDataParentFieldData.hasOwnProperty(currentPathName)) {
        const data =
          createdDataParentFieldData[
            currentPathName as keyof typeof createdDataParentFieldData
          ];

        if (Array.isArray(data) && data.length > 0) {
          const match = data.filter(item => item.fieldName === name);

          if (match.length > 0) {
            const tempSearchString = match[match.length - 1].searchString;

            setSearchString(tempSearchString);
            setDefaultOpts();

            let existingValues = undefined;

            if (formik && formik.values && formik.values.hasOwnProperty(name)) {
              const values = formik.values[name as keyof typeof formik.values];

              if (values && Array.isArray(values)) {
                existingValues = values;
              }
            }

            if (parsedSearchEndpoint) {
              getSearchResults(
                tempSearchString,

                parsedSearchEndpoint,
                match,
                selectedOptions ?? undefined,

                nestedParentFormData
              );
              setMenuOpen(true);
            } else {
              let tempNewOptions: SelectOption[] = [];

              let tempNewSelectedOptions: SelectOption[] = [];
              let tempNewFormSelectedOptions: number[] = [];

              match.forEach(item => {
                let parsedValue = item.id;
                if (parsedValue && typeof parsedValue === "string") {
                  parsedValue = parseInt(parsedValue);
                }

                if (parsedValue && typeof parsedValue === "number") {
                  tempNewOptions.push({
                    label: item.searchString,
                    value: parsedValue,
                  });

                  tempNewFormSelectedOptions.push(parsedValue);

                  tempNewSelectedOptions.push({
                    label: item.searchString,
                    value: parsedValue,
                  });
                }
              });

              if (options) {
                tempNewOptions = [...tempNewOptions, ...options];
              }

              if (selectedOptions) {
                tempNewFormSelectedOptions = [
                  ...tempNewFormSelectedOptions,
                  ...selectedOptions.map(item => item.value),
                ];

                tempNewSelectedOptions = [
                  ...tempNewSelectedOptions,
                  ...selectedOptions,
                ];
              }

              tempNewOptions = [...new Set(tempNewOptions)];
              tempNewFormSelectedOptions = [
                ...new Set(tempNewFormSelectedOptions),
              ];
              tempNewSelectedOptions = [...new Set(tempNewSelectedOptions)];

              setOptions(tempNewOptions);

              setSelectedOptions(tempNewSelectedOptions);

              formik.setFieldValue(name, tempNewFormSelectedOptions);

              if (
                dataSource &&
                formik.values &&
                formik.values.hasOwnProperty(dataSource)
              ) {
                let tempDataSource: any =
                  formik.values[dataSource as keyof typeof formik.values];

                tempDataSource = [...tempNewOptions, ...tempDataSource];

                tempDataSource = [...new Set(tempDataSource)];

                formik.setFieldValue(dataSource, tempDataSource);
              }
            }
          }
        }
      }
    }
  }, [
    reloadNestedFormParentFormToggle,
    createdDataParentFieldData,
    nestedParentFormData,
  ]);

  //if no endpoint filter meta list
  const onFilter = (e: any) => {
    e.preventDefault();
    let inputString = e.target.value;

    if (e.target.value === "") {
      setSearchString("");
      setDefaultOpts();
    } else if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setSearchString(inputString);
      setOptions(
        defaultOptions.filter((opt: SelectOption) =>
          opt.label && typeof opt.label === "string"
            ? opt.label.toLowerCase().includes(inputString.toLowerCase())
            : false
        ) ?? null
      );
    } else {
      setSearchString(e.target.value);
    }
  };

  //control links
  const handleAction = () => {
    saveFormReduxData(formik, dispatch);

    handleActionLinkClicked && handleActionLinkClicked(name);
  };
  const action = actionLinkConfig ? actionLinkConfig[name] : null;
  const link = linkTextConfig ? linkTextConfig[name] : null;
  const actionText = action ? action.text : "";
  const permissions = action
    ? useGetPermissions([action.permissionKey], action.location)
    : false;
  const linkPermissions = link
    ? useGetPermissions([link.permissionKey], link.location)
    : false;
  let hasPermission =
    permissions && action && permissions.get(action.permissionKey);
  let linkHasPermission =
    linkPermissions && link && linkPermissions.get(link.permissionKey);
  const linkTextStr = linkHasPermission && link ? link.text : "";
  // Fields that can't be edited in create/edit forms:

  const formReadOnlyFields = useSelector(selectFormReadOnlyFields);

  const editDisabled =
    name && formReadOnlyFields && formReadOnlyFields.hasOwnProperty(name);
  const editDisabledLabel =
    editDisabled &&
    formReadOnlyFields &&
    formReadOnlyFields.hasOwnProperty(name)
      ? formReadOnlyFields[name]
      : null;

  const relatedFormActionType = useSelector(selectRelatedFormActionType);

  // Fields that can't be edit but can have "Add new" buttons still active, from redirect form creation

  const allRelatedFormOriginFormAutoSelectValuesFields = useSelector(
    selectRelatedFormOriginFormAutoSelectAllValuesFields
  );

  const currentPathName = window.location.pathname;

  let autoSelectedValuesPartiallyDisabled = false;

  if (
    allRelatedFormOriginFormAutoSelectValuesFields &&
    Object.keys(allRelatedFormOriginFormAutoSelectValuesFields).length > 0 &&
    allRelatedFormOriginFormAutoSelectValuesFields.hasOwnProperty(
      currentPathName
    )
  ) {
    const data =
      allRelatedFormOriginFormAutoSelectValuesFields[currentPathName];

    data.forEach(item => {
      if (item.fieldName === name) {
        autoSelectedValuesPartiallyDisabled = true;
      }
    });
  }

  const selectedOptionEditDisabled = selectedOptions
    ? selectedOptions
        .map(item => (item && item.label ? item.label : null))
        .join(", ")
        .trim()
    : null;

  const relatedFormModuleName = useSelector(selectRelatedFormModuleName);

  const parsedCurrentPathName = `/${relatedFormModuleName}`;

  const relatedFormSuccessResponseIds = useSelector(
    selectRelatedFormSuccessResponseIds
  );

  let extraDataToDisplay: { [id: number]: string } = {};

  if (
    relatedFormSuccessResponseIds &&
    relatedFormSuccessResponseIds.hasOwnProperty(window.location.pathname)
  ) {
    const successData = relatedFormSuccessResponseIds[window.location.pathname];

    if (
      typeof successData === "object" &&
      successData.hasOwnProperty(name) &&
      Array.isArray(successData[name]) &&
      successData[name].length > 0
    ) {
      const ids = successData[name];

      ids.forEach(item => {
        const extraData = item.extraDataToDisplay;
        if (typeof extraData === "string") {
          extraDataToDisplay[item.id] = extraData;
        }
      });
    }
  }
  const handleNestedFormAction: (() => void) | null =
    nestedFormModule && nestedFormModule.hasOwnProperty(name)
      ? () => {
          const currentPathMame = window.location.pathname;
          const parentModuleName = currentPathMame.split("/")[1] as APP_ROUTES;

          const formValues = formik.values as Record<string, any>;
          const parentRecordId = formValues?.id;

          const parentFieldNameInChild = PARENT_CHILD_FIELD_MAPPING[name];

          if (!parentFieldNameInChild) {
            console.error(`No field mapping found for ${name}`);
            return;
          }

          // Set context for nested form creation
          if (parentRecordId && typeof parentRecordId === "number") {
            dispatch(
              setNestedFormCreationContext({
                parentModuleName: parentModuleName,
                parentRecordId: parentRecordId,
                parentFieldNameInChild: parentFieldNameInChild,
                targetModule: nestedFormModule[name],
              })
            );
          }

          const nestedFormData: NestedFormModuleData = {
            selectedId: undefined,
            actionType: "create",
            module: nestedFormModule[name],
            createdDataParentFieldName: name,
          };

          const newDataIds: any =
            formik.values &&
            typeof formik.values === "object" &&
            formik.values.hasOwnProperty(name)
              ? formik.values[name as keyof typeof formik.values]
              : null;

          const newData =
            newDataIds &&
            newDataIds &&
            options &&
            options.filter(item => newDataIds.includes(item.value)).length > 0
              ? options.filter(item => newDataIds.includes(item.value))
              : null;

          let allNewData = nestedParentFormData
            ? { ...nestedParentFormData }
            : {};

          if (allNewData.hasOwnProperty(nestedFormModule[name])) {
            let newModuleData = { ...allNewData[nestedFormModule[name]] };

            newModuleData[name] = newData;
            allNewData[nestedFormModule[name]] = newModuleData;
          } else {
            let newModuleData: any = {};

            newModuleData[name] = newData;
            allNewData[nestedFormModule[name]] = newModuleData;
          }

          dispatch(setNestedFormModuleData(nestedFormData));
        }
      : null;

  const onLinkNestedFormClick: ((fieldName: any, data: any) => void) | null =
    nestedFormModule && nestedFormModule.hasOwnProperty(name)
      ? (fieldName: any, data: any) => {
          const nestedFormData: NestedFormModuleData = {
            selectedId: data.value,
            actionType: "view",
            module: nestedFormModule[name],
            createdDataParentFieldName: name,
          };

          dispatch(setNestedFormModuleData(nestedFormData));
        }
      : null;

  return (
    <>
      <div
        className={`FormLabel MultiSelect ${
          editDisabled || editDisable ? "EditDisabled" : ""
        }
        ${
          autoSelectedValuesPartiallyDisabled
            ? "AutoSelectPartiallyDisabled"
            : ""
        }
        `}
      >
        <label>
          {`${label}`}{" "}
          {afterLabelText && (
            <span>
              <i className="pseudo">{afterLabelText}</i>
            </span>
          )}
        </label>

        {showEditIcon && (
          <img src={editIcon} alt="edit icon" className="EditIcon" />
        )}
      </div>
      <div className="FormField">
        <div
          className={`EditSingleSelectFieldContainer SearchListRenderer ${
            hasPermission && (handleActionLinkClicked || handleNestedFormAction)
              ? "WithLinks"
              : ""
          } ${name} ${editDisabled || editDisable ? "EditDisabled" : ""} ${
            autoSelectedValuesPartiallyDisabled
              ? "AutoSelectPartiallyDisabled"
              : ""
          }
          ${addZIndexToMenu ? "AddZIndexToMenu" : ""}
          `}
          style={maxHeight ? { maxHeight: `${maxHeight}` } : undefined}
          key={`MultiSelectSearchRenderer-${name}`}
          {...bind}
        >
          {editDisabled && (
            <div className="EditDisabledMessage">
              {relatedFormActionType === ActionTypes.CREATE
                ? `${editDisabledLabel ?? "Field"}  will be chosen after
          submitting`
                : `The related ${editDisabledLabel ?? "Field"}${
                    selectedOptionEditDisabled
                      ? ` '${selectedOptionEditDisabled}'`
                      : ""
                  } will be automatically chosen after submitting`}
            </div>
          )}

          {autoSelectedValuesPartiallyDisabled && (
            <div className="EditDisabledMessage">
              Values are automatically selected after creation
            </div>
          )}

          <Select
            className={`CustomSelect EditSingleSelect SearchListRenderer ${
              singleSelect ? "Single" : ""
            }`}
            classNamePrefix={`CustomSelect${hasError ? "Error" : ""}`}
            options={options ?? []}
            value={selectedOptions}
            isSearchable={false}
            placeholder={false}
            onChange={
              onChange as (selectedOption: ValueType<SelectOption, any>) => void
            }
            selectProps={{
              fieldName: name,
              onLinkClick: onLinkClick,
              bOnLinkClick: link?.permissionKey === "overview",
              onLinkNestedFormClick: onLinkNestedFormClick,
              linkText: linkTextStr,
              menuOpen: menuOpen,
              setMenuOpen: setMenuOpen,
              extraDataToDisplay: extraDataToDisplay,
              expandableInfo: options,
              expandable,
              selectedOptions: selectedOptions,
              searchString,
              parsedSearchEndpoint,
              fetchResults,
              callSearchEndpointOnClick,
            }}
            noOptionsMessage={() => "No options"}
            menuIsOpen={menuOpen}
            blurInputOnSelect={false}
            closeMenuOnScroll={true}
            onMenuOpen={() => {
              setMenuOpen(true); // NOTE: This is needed to make click on mobile phones work
            }}
            onMenuClose={() => {
              // NOTE: The setMenuOpen line below is not needed here, otherwise it will close the dropdown too early, it is needed on none search list dropdowns though
              // setMenuOpen(false);
            }}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option: CustomOptionWithLink,
              Control: CustomControl,
            }}
            isMulti={!singleSelect}
            key={`MultiSelectSearch-${name}`}
            filterOption={parsedSearchEndpoint ? filterPlaceholder : undefined}
            backspaceRemovesValue={false} // Prevent backspace key presses deleting selected items
            styles={{
              multiValue: (styles, { data }) => {
                return {
                  ...styles,
                  backgroundColor: "#DEEBFF",
                  color: "#4C9AFF",
                };
              },
              multiValueLabel: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                fontSize: "1.1rem",
              }),
              multiValueRemove: (styles, { data }) => ({
                ...styles,
                color: "#4C9AFF",
                ":hover": {
                  backgroundColor: "red",
                  color: "white",
                },
              }),
            }}
          />
          {/*search bar overlay*/}
          <div
            className={`${menuOpen ? "behind" : "hide"} ${
              hasPermission &&
              (handleActionLinkClicked || handleNestedFormAction)
                ? "WithLinks"
                : ""
            } ${editDisabled ? "EditDisabled" : ""} ${
              autoSelectedValuesPartiallyDisabled
                ? "AutoSelectPartiallyDisabled"
                : ""
            }`}
          >
            <input
              className="custom-input-search"
              value={searchString}
              onChange={parsedSearchEndpoint ? onSearch : onFilter}
              placeholder="Type search here"
              onFocus={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = false;
                setTouched(newTouched);
              }}
              onBlur={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = true;
                setTimeout(() => {
                  setTouched(newTouched);
                }, 100);
              }}
            />

            {hasPermission && handleNestedFormAction && (
              <div onClick={handleNestedFormAction} className="Link">
                {actionText ?? "Action"}
              </div>
            )}

            {hasPermission && handleAction && !handleNestedFormAction && (
              <div onClick={handleAction} className="Link">
                {actionText ?? "Action"}
              </div>
            )}
          </div>
          {/*formik error */}
          {hasError && (
            <div className="ErrorLabel" key={`ErrorLabel-${name}`}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export const SingleSelectSearchListWithLinks: React.FC<SingleSelectSearchListWithLinksProps> = ({
  name,
  label,
  dataSource,
  searchEndpoint,
  handleActionLinkClicked,
  onLinkClick,
  actionLinkConfig,
  linkTextConfig,
  singleSelect = false,
  afterLabelText,
  readOnly,
  nestedFormModule,
  optionalSearchParameters,
  overrideOptions,
  showEditIcon,
  maxHeight,
  addZIndexToMenu,
  callSearchEndpointOnClick,
}) => {
  const [parsedSearchEndpoint, setParsedSearchEndpoint] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (searchEndpoint) {
      if (
        typeof searchEndpoint === "object" &&
        searchEndpoint.hasOwnProperty(name)
      ) {
        setParsedSearchEndpoint(
          searchEndpoint[name as keyof typeof searchEndpoint]
        );
      } else if (typeof searchEndpoint === "string") {
        setParsedSearchEndpoint(searchEndpoint);
      }
    }
  }, [searchEndpoint]);

  const [{ value }, { touched, error }, { setValue }] = useField<number | null>(
    name
  );

  // Fields that can't be edited in create/edit forms:

  const formReadOnlyFields = useSelector(selectFormReadOnlyFields);

  const editDisabled =
    name && formReadOnlyFields && formReadOnlyFields.hasOwnProperty(name);
  const editDisabledLabel =
    editDisabled &&
    formReadOnlyFields &&
    formReadOnlyFields.hasOwnProperty(name)
      ? formReadOnlyFields[name]
      : null;

  const relatedFormActionType = useSelector(selectRelatedFormActionType);

  // Fields that can't be edit but can have "Add new" buttons still active, from redirect form creation

  const allRelatedFormOriginFormAutoSelectValuesFields = useSelector(
    selectRelatedFormOriginFormAutoSelectAllValuesFields
  );

  const currentPathName = window.location.pathname;

  let autoSelectedValuesPartiallyDisabled = false;

  if (
    allRelatedFormOriginFormAutoSelectValuesFields &&
    Object.keys(allRelatedFormOriginFormAutoSelectValuesFields).length > 0 &&
    allRelatedFormOriginFormAutoSelectValuesFields.hasOwnProperty(
      currentPathName
    )
  ) {
    const data =
      allRelatedFormOriginFormAutoSelectValuesFields[currentPathName];

    data.forEach(item => {
      if (item.fieldName === name) {
        autoSelectedValuesPartiallyDisabled = true;
      }
    });
  }

  const [options, setOptions] = useState<SelectOption[] | null>(null);

  const [selectedOption, setSelectedOption] = useState<SelectOption | null>(
    null
  );
  const [searchString, setSearchString] = useState("");

  const dispatch = useDispatch();

  const {
    values: formValues,
    setTouched,
    touched: formikTouched,
    setFieldValue,
    submitCount,
  } = useFormikContext<{
    [key: string]: any;
  }>();
  const defaultOptions = formValues[dataSource as keyof typeof formValues];

  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const bind = customUseOnClickOutside(() => setMenuOpen(false), true);

  const hasError = submitCount > 0 && error;

  useEffect(() => {
    setMenuOpen(false);
  }, [selectedOption]);

  useEffect(() => {
    setDefaultOpts();
  }, []);

  useOnceAfterCondition(
    () => {
      if (!value) return;

      if (!Array.isArray(value)) {
        const startOps = options?.filter(opt => value === opt.value);
        setSelectedOption(startOps && startOps.length > 0 ? startOps[0] : null);
      } else if (Array.isArray(value) && value.length > 0) {
        const startOps = options?.filter(opt => value.includes(opt.value));
        setSelectedOption(startOps && startOps.length > 0 ? startOps[0] : null);

        const optionsWithNoMatch = options?.filter(
          opt => !value.includes(opt.value)
        );

        if (
          parsedSearchEndpoint &&
          optionsWithNoMatch &&
          optionsWithNoMatch.length === 1
        ) {
          getSearchResults(
            "",
            parsedSearchEndpoint,
            undefined,
            optionsWithNoMatch[0]
          );
        }
      }
    },
    [options],
    options
  );

  const onChange = (v: SelectOption) => {
    let newOptions: any = v;

    if (!Array.isArray(newOptions)) {
      setSelectedOption(newOptions);
    }

    if (singleSelect) {
      if (Array.isArray(v) && v.length > 0) {
        setValue(v[0].value);
        setFieldValue(name, v[0].value);
      } else if (typeof v === "object" && v.hasOwnProperty("value")) {
        setValue(v["value" as keyof typeof v]);
        setFieldValue(name, v["value" as keyof typeof v]);
      } else {
        setValue(null);
        setFieldValue(name, null);
      }
    }
  };

  const fetchResults = async (
    searchString: string,
    parsedSearchEndpoint: string,
    createdDataParentFieldData?: CreatedDataParentFieldDataItem[],
    existingValues?: any
  ) => {
    if (parsedSearchEndpoint) {
      try {
        let parameters: any = { searchString };

        const parsedOptionalSearchParameters = getOptionalSearchParameters(
          optionalSearchParameters,
          formik,
          name
        );
  
        if (parsedOptionalSearchParameters) {
          parameters = { ...parameters, ...parsedOptionalSearchParameters };
        }

        const results = await api.get(parsedSearchEndpoint, {
          params: parameters,
        });
        let options: SelectOption[] = [];

        if (
          results &&
          results.data &&
          results.data.status === "1" &&
          results.data.details.results &&
          Array.isArray(results.data.details.results)
        ) {
          options = results.data.details.results;
        }

        if (createdDataParentFieldData) {
          let newSelectedOption: SelectOption | null = createdDataParentFieldData
            ? createdDataParentFieldData.map(item => {
                return { label: item.searchString, value: item.id };
              })[0]
            : null;

          if (!newSelectedOption && selectedOption) {
            newSelectedOption = selectedOption;
          }

          setSelectedOption(newSelectedOption);

          let id: string | number = newSelectedOption?.value;

          if (typeof id === "string") {
            id = parseInt(id);
          }

          formik.setFieldValue(name, id);
        }

        if (searchString === "" && existingValues) {
          const tempExistingValuesInSearchResults = options.filter(
            item => existingValues === item.value
          );

          options = [...options, ...tempExistingValuesInSearchResults];

          let newSelectedOption =
            tempExistingValuesInSearchResults.length === 1
              ? tempExistingValuesInSearchResults[0]
              : undefined;

          if (newSelectedOption) {
            setSelectedOption(newSelectedOption);
          }

          let newFormSelectedOption = newSelectedOption;

          formik.setFieldValue(name, newFormSelectedOption);
        }

        setOptions(options);
      } catch (error) {
        // Error case
        const err: any = error;
        if (
          err.response &&
          err.response.data &&
          err.response.data.errors &&
          Array.isArray(err.response.data.errors)
        ) {
          console.log("err.response.data.errors: ", err.response.data.errors);
        }
      }
    }
  };

  useStoreProgrammmeId(currentPathName, name, value);

  const getSearchResults = useCallback(debounce(fetchResults, 500), []);

  const setDefaultOpts = () => {
    if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setOptions(defaultOptions);
    } else {
      setOptions([]);
    }
  };

  // Nested form auto search data after creating new item:

  const reloadNestedFormParentFormToggle = useSelector(
    selectReloadNestedFormParentFormToggle
  );
  const createdDataParentFieldData = useSelector(
    selectCreatedDataParentFieldData
  );

  useEffect(() => {
    if (reloadNestedFormParentFormToggle && createdDataParentFieldData) {
      const currentPathName = window.location.pathname;

      if (createdDataParentFieldData.hasOwnProperty(currentPathName)) {
        const data =
          createdDataParentFieldData[
            currentPathName as keyof typeof createdDataParentFieldData
          ];

        if (Array.isArray(data) && data.length > 0) {
          const match = data.filter(item => item.fieldName === name);

          if (match.length > 0) {
            const searchstring = match[match.length - 1].searchString;

            setSearchString(searchstring);
            setDefaultOpts();

            if (parsedSearchEndpoint) {
              getSearchResults(searchString, parsedSearchEndpoint, match);
              setMenuOpen(true);
            } else {
              let tempNewOptions: SelectOption[] = [];

              let tempNewSelectedOption: SelectOption | null = null;
              let tempNewFormSelectedOption: number | null = null;

              // Should only be 1 match
              match.forEach(item => {
                let parsedValue = item.id;
                if (parsedValue && typeof parsedValue === "string") {
                  parsedValue = parseInt(parsedValue);
                }

                if (parsedValue && typeof parsedValue === "number") {
                  tempNewOptions.push({
                    label: item.searchString,
                    value: parsedValue,
                  });

                  tempNewFormSelectedOption = parsedValue;

                  tempNewSelectedOption = {
                    label: item.searchString,
                    value: parsedValue,
                  };
                }
              });

              if (options) {
                tempNewOptions = [...tempNewOptions, ...options];
              }

              tempNewOptions = [...new Set(tempNewOptions)];

              setOptions(tempNewOptions);

              setSelectedOption(tempNewSelectedOption);

              formik.setFieldValue(name, tempNewFormSelectedOption);

              if (
                dataSource &&
                formik.values &&
                formik.values.hasOwnProperty(dataSource)
              ) {
                let tempDataSource: any =
                  formik.values[dataSource as keyof typeof formik.values];

                tempDataSource = [...tempNewOptions, ...tempDataSource];

                tempDataSource = [...new Set(tempDataSource)];

                formik.setFieldValue(dataSource, tempDataSource);
              }
            }
          }
        }
      }
    }
  }, [reloadNestedFormParentFormToggle, createdDataParentFieldData]);

  const onFilter = (e: any) => {
    e.preventDefault();

    let inputString = e.target.value;
    if (e.target.value === "") {
      setSearchString("");
      setDefaultOpts();
    } else if (
      defaultOptions &&
      defaultOptions.length >= 1 &&
      defaultOptions[0].hasOwnProperty("label") &&
      defaultOptions[0].hasOwnProperty("value")
    ) {
      setSearchString(inputString);
      setOptions(
        defaultOptions.filter((opt: SelectOption) =>
          opt.label && typeof opt.label === "string"
            ? opt.label.toLowerCase().includes(inputString.toLowerCase())
            : false
        ) ?? null
      );
    } else {
      setSearchString(e.target.value);
    }
  };

  const onSearch = (e: any) => {
    e.preventDefault();
    if (e.target.value === "") {
      setSearchString("");
      setDefaultOpts();

      if (parsedSearchEndpoint) {
        getSearchResults("", parsedSearchEndpoint);
      }
    } else if (parsedSearchEndpoint && e.target.value) {
      setSearchString(e.target.value);
      getSearchResults(e.target.value, parsedSearchEndpoint);
    }
  };

  const Remove = () => {
    setSelectedOption(null);
    setFieldValue(name, null);
  };

  const filterPlaceholder = (option: any, rawInput: string) => {
    return true;
  };

  const formik = useFormikContext();

  useEffect(() => {
    if (overrideOptions && overrideOptions.hasOwnProperty(name)) {
      const overrideFunction = overrideOptions[name];
      const [tempOptions, resetField, triggerField] = overrideFunction(formik);

      if (
        value !== null &&
        !tempOptions.some(
          item => typeof value === "number" && item.value === value
        )
      ) {
        setFieldValue(name, null);
        setSelectedOption(null);
        toast(
          <ToastMessage
            toastData={[
              {
                title: `${resetField} Reset`,
                text: `${resetField} no longer available for chosen ${triggerField}`,
              },
            ]}
            className="ToastWarning"
          />,
          {
            ...toastConfig,
          }
        );
      }
      setOptions(tempOptions);
    }
  }, [overrideOptions, formik.values]);

  const handleAction = () => {
    saveFormReduxData(formik, dispatch);

    handleActionLinkClicked && handleActionLinkClicked(name);
  };
  const action = actionLinkConfig ? actionLinkConfig[name] : null;
  const link = linkTextConfig ? linkTextConfig[name] : null;
  const actionText = action ? action.text : "";
  const permissions = action
    ? useGetPermissions([action.permissionKey], action.location)
    : false;
  const linkPermissions = link
    ? useGetPermissions([link.permissionKey], link.location)
    : false;
  let hasPermission =
    permissions && action && permissions.get(action.permissionKey);
  let linkHasPermission =
    linkPermissions && link && linkPermissions.get(link.permissionKey);
  const linkTextStr = linkHasPermission && link ? link.text : "";

  const relatedFormModuleName = useSelector(selectRelatedFormModuleName);

  let disableAllAddAndViewLinks = false;

  const parsedCurrentPathName = `/${relatedFormModuleName}`;

  if (
    relatedFormModuleName &&
    window.location.pathname !== parsedCurrentPathName
  ) {
    disableAllAddAndViewLinks = true;
  }

  const relatedFormSuccessResponseIds = useSelector(
    selectRelatedFormSuccessResponseIds
  );

  let extraDataToDisplay: { [id: number]: string } = {};

  if (
    relatedFormSuccessResponseIds &&
    relatedFormSuccessResponseIds.hasOwnProperty(window.location.pathname)
  ) {
    const successData = relatedFormSuccessResponseIds[window.location.pathname];

    if (
      typeof successData === "object" &&
      successData.hasOwnProperty(name) &&
      Array.isArray(successData[name]) &&
      successData[name].length > 0
    ) {
      const ids = successData[name];

      ids.forEach(item => {
        const extraData = item.extraDataToDisplay;
        if (typeof extraData === "string") {
          extraDataToDisplay[item.id] = extraData;
        }
      });
    }
  }

  const handleNestedFormAction: (() => void) | null =
    nestedFormModule && nestedFormModule.hasOwnProperty(name)
      ? () => {
          const currentPathMame = window.location.pathname;
          const parentModuleName = currentPathMame.split("/")[1] as APP_ROUTES;

          const formValues = formik.values as Record<string, any>;
          const parentRecordId = formValues?.id;

          const parentFieldNameInChild = PARENT_CHILD_FIELD_MAPPING[name];

          if (!parentFieldNameInChild) {
            console.error(`No field mapping found for ${name}`);
            return;
          }

          // Set context for nested form creation
          if (parentRecordId && typeof parentRecordId === "number") {
            dispatch(
              setNestedFormCreationContext({
                parentModuleName: parentModuleName,
                parentRecordId: parentRecordId,
                parentFieldNameInChild: parentFieldNameInChild,
                targetModule: nestedFormModule[name],
              })
            );
          }

          const nestedFormData: NestedFormModuleData = {
            selectedId: undefined,
            actionType: "create",
            module: nestedFormModule[name],
            createdDataParentFieldName: name,
          };

          dispatch(setNestedFormModuleData(nestedFormData));
        }
      : null;

  const onLinkNestedFormClick: ((fieldName: any, data: any) => void) | null =
    nestedFormModule && nestedFormModule.hasOwnProperty(name)
      ? (fieldName: any, data: any) => {
          const nestedFormData: NestedFormModuleData = {
            selectedId: data.value,
            actionType: "view",
            module: nestedFormModule[name],
            createdDataParentFieldName: name,
          };

          dispatch(setNestedFormModuleData(nestedFormData));
        }
      : null;

  return (
    <>
      <div
        className={`FormLabel MultiSelect ${editDisabled ? "EditDisabled" : ""}
        ${
          autoSelectedValuesPartiallyDisabled
            ? "AutoSelectPartiallyDisabled"
            : ""
        }
        ${disableAllAddAndViewLinks ? "DisabledAllAddAndViewLinks" : ""}
        `}
      >
        <label>
          {`${label}`}{" "}
          {afterLabelText && (
            <span>
              <i className="pseudo">{afterLabelText}</i>
            </span>
          )}
        </label>

        {showEditIcon && (
          <img src={editIcon} alt="edit icon" className="EditIcon" />
        )}
      </div>
      <div className="FormField">
        <div
          className={`EditSingleSelectFieldContainer SearchListRenderer ${
            hasPermission && (handleActionLinkClicked || handleNestedFormAction)
              ? "WithLinks"
              : ""
          } ${hasError ? "Error" : ""} ${name} ${
            editDisabled ? "EditDisabled" : ""
          }  ${disableAllAddAndViewLinks ? "DisabledAllAddAndViewLinks" : ""} ${
            readOnly ? "EditReadOnly" : ""
          }
          ${addZIndexToMenu ? "AddZIndexToMenu" : ""}
          `}
          style={maxHeight ? { maxHeight: `${maxHeight}` } : undefined}
          key={`MultiSelectSearchRenderer-${name}`}
          {...bind}
        >
          {editDisabled && (
            <div className="EditDisabledMessage">
              {relatedFormActionType === ActionTypes.CREATE
                ? `${editDisabledLabel ?? "Field"} will be chosen after
          submitting`
                : `The related ${editDisabledLabel ?? "Field"}${
                    selectedOption ? ` '${selectedOption.label}'` : ""
                  } will be automatically chosen after submitting`}
            </div>
          )}

          <Select
            className={`CustomSelect EditSingleSelect SearchListRenderer Single`}
            classNamePrefix={`CustomSelect${hasError ? "Error" : ""}`}
            options={options ?? []}
            value={selectedOption}
            isSearchable={false}
            onChange={
              onChange as (selectedOption: ValueType<SelectOption, any>) => void
            }
            selectProps={{
              fieldName: name,
              onLinkClick: onLinkClick,
              linkText: linkTextStr,
              menuOpen: menuOpen,
              setMenuOpen: setMenuOpen,
              remove: Remove,
              extraDataToDisplay: extraDataToDisplay,
              onLinkNestedFormClick: onLinkNestedFormClick,
              searchString,
              parsedSearchEndpoint,
              fetchResults,
              callSearchEndpointOnClick,
            }}
            noOptionsMessage={() => "No options"}
            menuIsOpen={menuOpen}
            onMenuOpen={() => {
              setMenuOpen(true); // NOTE: This is needed to make click on mobile phones work
            }}
            onMenuClose={() => {
              // NOTE: The setMenuOpen line below is not needed here, otherwise it will close the dropdown too early, it is needed on none search list dropdowns though
              //   setMenuOpen(false);
            }}
            placeholder={false}
            closeMenuOnSelect={false}
            hideSelectedOptions={false}
            components={{
              Option: CustomOptionWithLink,
              Control: CustomControl,
              SingleValue: CustomValue,
            }}
            isMulti={false}
            key={`MultiSelectSearch-${name}`}
            filterOption={parsedSearchEndpoint ? filterPlaceholder : undefined}
            onBlur={() => {
              let newTouched = { ...formikTouched };
              newTouched[name] = true;
              setTouched(newTouched);
            }}
            backspaceRemovesValue={false} // Prevent backspace key presses deleting selected items
            styles={{
              singleValue: (styles, { data }) => {
                return {
                  ...styles,
                  backgroundColor: "#DEEBFF",
                  color: "#4C9AFF",
                };
              },
            }}
          />
          <div
            className={`${menuOpen ? "behind" : "hide"} ${
              hasPermission &&
              (handleActionLinkClicked || handleNestedFormAction)
                ? "WithLinks"
                : ""
            } ${editDisabled ? "EditDisabled" : ""} ${
              autoSelectedValuesPartiallyDisabled
                ? "AutoSelectPartiallyDisabled"
                : ""
            }`}
          >
            <input
              className="custom-input-search"
              value={searchString}
              onChange={parsedSearchEndpoint ? onSearch : onFilter}
              placeholder="Type search here"
              onFocus={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = false;
                setTouched(newTouched);
              }}
              onBlur={() => {
                let newTouched = { ...formikTouched };
                newTouched[name] = true;
                setTimeout(() => {
                  setTouched(newTouched);
                }, 100);
              }}
            />

            {hasPermission && handleNestedFormAction && (
              <div onClick={handleNestedFormAction} className="Link">
                {actionText ?? "Action"}
              </div>
            )}

            {hasPermission && handleAction && !handleNestedFormAction && (
              <div onClick={handleAction} className="Link">
                {actionText ?? "Action"}
              </div>
            )}
          </div>

          {hasError && (
            <div className="ErrorLabel" key={`ErrorLabel-${name}`}>
              {error}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
